/**
 * 中控看板时间窗的算式与状态机（纯函数，不碰取数）。
 *
 * 两组来源归一成同一枚状态：用户从共用时间范围控件选出的起止（快捷或手选）＝窗口态，只按起止日期取数；
 * 旧入口只带 `?period=`（历史收藏、旧下钻链接）＝兼容态，周期中文名与取数口既有口径一字未动。
 * 看板页与漏斗下钻页都从这一枚状态读，⛔ 不各推一份（两处一分叉，同一天就会出现两个数）。
 * 六枚快捷的算式只在 `./dateRange` 一处，这里只负责把「用户选了什么」翻成取数参数与展示文案。
 * 副标题的窗口文案还吃看板响应里的 `window` 回执：有回执就用回执的起止与天数（滚动窗的天数以回执为准），
 * 后端没回或回执残缺时才回退上面的本地算式。
 */
import { addDays, cstToday, isDateText, quickRange, rangeText, type DateRange } from './dateRange';

/** 取数口受理的周期三值：缺失／空串／未知一律兜 today（与既有语义逐字一致，⛔ 不改判 400） */
export type PeriodKey = 'today' | 'week' | 'month';

/** 周期中文名（与取数口既有文案逐字一致，页面副标题与漏斗副标题共用） */
export const PERIOD_LABEL: Record<PeriodKey, string> = {
  today: '今日',
  week: '本周',
  month: '本月',
};

/** 取数参数里与窗口有关的三项（地域等其它维度由调用方另拼） */
export interface DashboardWindowParams {
  period?: PeriodKey;
  from?: string;
  to?: string;
}

/** 宽松入参：地址栏解析出来的值、或调用方手上的取数参数都能直接喂进来 */
export interface DashboardWindowQuery {
  period?: string | null;
  from?: string | null;
  to?: string | null;
}

export interface DashboardRangeState {
  from: string;
  to: string;
  period: PeriodKey;
  /** true＝起止区间生效；false＝按 period 的兼容态 */
  window: boolean;
}

/** 周期取值白名单：'week'／'month' 之外（含缺失、空串、未知值）一律 today */
export function periodOfQuery(raw: unknown): PeriodKey {
  return raw === 'week' || raw === 'month' ? raw : 'today';
}

/**
 * 兼容态在控件上摆出的起止日期（只用于显示，⛔ 不改写取数语义）。
 *
 * 与取数口周期族窗口对齐：today＝当日；week＝含今日的 7 个自然日；month＝含今日的 30 个自然日
 * （week／month 是滚动窗、不是自然月，故这里也按滚动窗摆，⛔ 不摆成自然月）。
 */
export function periodRangeWindow(period: PeriodKey, today: string = cstToday()): DateRange {
  if (period === 'week') return { from: addDays(today, -6), to: today };
  if (period === 'month') return { from: addDays(today, -29), to: today };
  return quickRange('today', today);
}

/** 后端窗口回执（看板三口径 additive 增的 `window`）归一后的形状 */
export interface DashboardWindowInfo {
  from: string;
  to: string;
  days: number;
  basis: string;
}

/** 回执的周期基：既有周期族映射到周期中文名，自选区间等其它基不加前缀 */
const BASIS_PERIOD: Record<string, PeriodKey> = {
  PERIOD_TODAY: 'today',
  PERIOD_WEEK: 'week',
  PERIOD_MONTH: 'month',
};

/**
 * 把后端回执里的窗口归一成可展示的形状。
 *
 * 起止合法（含次序）且天数有限、不小于 1 才认；缺失、空值、字段残缺、类型不符一律返回 null，
 * 交给调用方回退本地算式 —— ⛔ 宁可只显示周期中文名，也不把 undefined／NaN 摆到副标题上。
 */
export function normalizeDashboardWindow(raw: unknown): DashboardWindowInfo | null {
  if (!raw || typeof raw !== 'object') return null;
  const source = raw as Record<string, unknown>;
  const from = source.from;
  const to = source.to;
  if (!isDateText(from) || !isDateText(to) || from > to) return null;
  const days = source.days;
  if (typeof days !== 'number' || !Number.isFinite(days) || days < 1) return null;
  return { from, to, days: Math.trunc(days), basis: typeof source.basis === 'string' ? source.basis : '' };
}

/**
 * 兼容态副标题的窗口文案：周期中文名（按回执基）＋起止＋**回执给的天数**。
 *
 * 天数是回执说了算：既有 `period=week` 是含今日的 8 个自然日滚动窗（下界昨日往前 7 日、含今日），
 * ⛔ 不按本地那套「含今日 7 日」的摆位算式写成 7 日。回执不可用 ⇒ null，调用方回退周期中文名。
 */
export function dashboardWindowLabel(raw: unknown): string | null {
  const info = normalizeDashboardWindow(raw);
  if (!info) return null;
  const period = BASIS_PERIOD[info.basis];
  const prefix = period ? PERIOD_LABEL[period] + ' · ' : '';
  const from = info.from;
  const to = info.to;
  const days = info.days;
  return `${prefix}${rangeText({ from, to })} · 共 ${days} 日`;
}

/**
 * 取数窗口的展示文案：起止齐给 ⇒ 起止文案（窗口态逐字不变）；
 * 否则优先用后端回执的窗口，后端未回（或缺字段）时回退周期中文名。
 */
export function rangeLabelOfQuery(query: DashboardWindowQuery, rawWindow?: unknown): string {
  if (isDateText(query.from) && isDateText(query.to)) {
    return rangeText({ from: query.from, to: query.to });
  }
  return dashboardWindowLabel(rawWindow) ?? PERIOD_LABEL[periodOfQuery(query.period)];
}

/** 地址栏参数 → 页面时间窗：两端齐全且次序正确才算窗口态，否则回落周期兼容态（地址栏是唯一真源） */
export function dashboardRangeOfQuery(
  query: Record<string, unknown>,
  today: string = cstToday(),
): DashboardRangeState {
  const period = periodOfQuery(query.period);
  const from = query.from;
  const to = query.to;
  if (isDateText(from) && isDateText(to) && from <= to) {
    return { from, to, period, window: true };
  }
  const shown = periodRangeWindow(period, today);
  return { from: shown.from, to: shown.to, period, window: false };
}

/** 页面时间窗 → 取数参数：窗口态只给起止（⛔ 不再单独送周期），兼容态只给周期 */
export function dashboardRangeQuery(state: DashboardRangeState): DashboardWindowParams {
  return state.window ? { from: state.from, to: state.to } : { period: state.period };
}

/** 下钻链接参数：窗口态带起止（周期仅作兼容保留），只带周期的旧入口逐字不变 */
export function funnelDrillLinkQuery(state: DashboardRangeState): Record<string, string> {
  return state.window
    ? { from: state.from, to: state.to, period: state.period }
    : { period: state.period };
}

/** 页面副标题用的窗口文案：窗口态＝起止文案，兼容态＝周期中文名（与取数口径同一枚真源） */
export function dashboardRangeLabel(state: DashboardRangeState): string {
  return state.window ? rangeText({ from: state.from, to: state.to }) : PERIOD_LABEL[state.period];
}
